import React from 'react';
import { LuInfo } from 'react-icons/lu';

import { cn } from '@/utils/cn';

import { findGlossaryTerm, GlossaryKey } from './glossary';
import { useReportingGlossary } from './useReportingGlossary';

/**
 * The `i` beside a metric's label that opens the glossary on it: the Portfolio Reporting redesign's info icon
 * (stone-500 there, the nearest cocoa here).
 */
export const GlossaryInfoButton = ({ termKey }: { termKey: GlossaryKey }) => {
    const { openGlossary } = useReportingGlossary();
    return (
        <button
            type="button"
            aria-label={`About ${findGlossaryTerm(termKey)?.name}`}
            onClick={() => openGlossary(termKey)}
            className="text-cocoa-400 hover:text-cocoa-900 focus-visible:ring-ring rounded-sm focus-visible:ring-2 focus-visible:outline-none"
        >
            <LuInfo className="h-3.5 w-3.5" />
        </button>
    );
};

/**
 * A metric's label followed by its info icon. Only metrics with a glossary entry render the icon, so the others need
 * no router (it reads the URL). Forwards its ref and props so it can be a tooltip trigger (HeroStatCard).
 */
export const GlossaryLabel = React.forwardRef<
    HTMLSpanElement,
    React.HTMLAttributes<HTMLSpanElement> & { label: string; glossaryKey?: GlossaryKey }
>(({ label, glossaryKey, className, ...props }, ref) => (
    <span ref={ref} {...props} className={cn('inline-flex items-center gap-1.5', className)}>
        {label}
        {glossaryKey ? <GlossaryInfoButton termKey={glossaryKey} /> : null}
    </span>
));
GlossaryLabel.displayName = 'GlossaryLabel';
